import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import MuiLanIcon from '@mui/icons-material/Lan';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import MuiDescriptionIcon from '@mui/icons-material/Description';

import { escPosTransportOf, type PrinterStatus, ESCPOS_PROFILE_PRESETS } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import type { EscPosCandidateState } from './use-escpos-printer-setup';
import { escPosKindLabel, escPosEndpointLabel } from '../printer-interface-meta';

// ----------------------------------------------------------------------

const SERIAL_BAUD_RATES = [9600, 19200, 38400, 57600, 115200] as const;

interface EscPosCandidatePanelProps {
  candidate: EscPosCandidateState;
  name: string;
  presetId: string;
  saving: boolean;
  baudRate: number;
  onBaudRateChange: (baudRate: number) => void;
  onNameChange: (name: string) => void;
  onPresetChange: (id: string) => void;
  onTestPrint: () => void;
  onSave: () => void;
  onSaveAndUse: () => void;
  onBack: () => void;
}

type ChipColor = 'success' | 'error' | 'warning' | 'default';

/**
 * A printer that cannot report its condition is shown as exactly that — the
 * chips never claim paper or cover are fine when the printer never said so.
 */
function statusChips(status: PrinterStatus | undefined): { label: string; color: ChipColor }[] {
  if (!status) return [];
  if (status.statusReporting === 'unsupported') {
    return [{ label: 'Does not report paper or cover status', color: 'default' }];
  }
  if (status.statusReporting === 'limited') {
    const chips: { label: string; color: ChipColor }[] = [{ label: 'Status from Windows only', color: 'default' }];
    if (status.coverOpen) chips.push({ label: 'Cover open (Windows)', color: 'warning' });
    if (status.paperEmpty) chips.push({ label: 'No paper (Windows)', color: 'error' });
    if (!status.online) chips.push({ label: 'Offline (Windows)', color: 'error' });
    return chips;
  }
  if (status.statusReporting === 'unknown' || (status.message !== undefined && status.cutterError === undefined)) {
    return [{ label: 'Paper and cover status not reported yet', color: 'default' }];
  }
  const chips: { label: string; color: ChipColor }[] = [
    status.online ? { label: 'Online', color: 'success' } : { label: 'Offline', color: 'error' },
  ];
  if (status.coverOpen) chips.push({ label: 'Cover open', color: 'warning' });
  if (status.paperEmpty) chips.push({ label: 'No paper', color: 'error' });
  else if (status.paperNearEmpty) chips.push({ label: 'Low paper', color: 'warning' });
  return chips;
}

export default function EscPosCandidatePanel({
  candidate,
  name,
  presetId,
  saving,
  baudRate,
  onBaudRateChange,
  onNameChange,
  onPresetChange,
  onTestPrint,
  onSave,
  onSaveAndUse,
  onBack,
}: EscPosCandidatePanelProps) {
  const { printer, check, test } = candidate;
  const busy = check === 'checking' || test === 'printing' || saving;
  const tested = test === 'printed';
  const chips = statusChips(candidate.status);
  const transport = escPosTransportOf(printer);

  return (
    <Stack spacing={2} sx={{ px: 3, pb: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', width: 48, height: 48 }}>
          <MuiIcon icon={MuiLanIcon} width={24} />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">{escPosKindLabel(printer)} (ESC/POS)</Typography>
          <Typography variant="body2" color="text.secondary">
            {escPosEndpointLabel(printer)}
          </Typography>
        </Box>
        {candidate.identified ? (
          <Chip label="Answers as ESC/POS" size="small" color="success" variant="soft" />
        ) : (
          <Chip label="Unconfirmed" size="small" color="warning" variant="soft" />
        )}
      </Stack>

      {check === 'checking' && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Connecting to {escPosEndpointLabel(printer)}…</Typography>
        </Stack>
      )}

      {check === 'reachable' && (
        <Alert severity="success">
          <Typography variant="body2" sx={{ mb: chips.length ? 1 : 0 }}>
            The printer accepted the connection.
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {chips.map((chip) => (
              <Chip key={chip.label} label={chip.label} color={chip.color} size="small" variant="soft" />
            ))}
          </Stack>
        </Alert>
      )}

      {check === 'unreachable' && (
        <Alert severity="error">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {candidate.checkError}
          </Typography>
          <Typography variant="caption" component="div">
            Check the printer is on, its network light is lit, and the port matches the printer’s
            network settings (usually 9100).
          </Typography>
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          size="small"
          label="Name (optional)"
          placeholder="Front counter"
          value={name}
          disabled={busy}
          onChange={(event) => onNameChange(event.target.value)}
        />
        <TextField
          select
          fullWidth
          size="small"
          label="Printer profile"
          value={presetId}
          disabled={busy}
          onChange={(event) => onPresetChange(event.target.value)}
          helperText={ESCPOS_PROFILE_PRESETS.find((preset) => preset.id === presetId)?.description}
        >
          {ESCPOS_PROFILE_PRESETS.map((preset) => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.label}
            </MenuItem>
          ))}
        </TextField>
        {transport === 'serial' && (
          <TextField
            select
            size="small"
            label="Baud rate"
            value={baudRate}
            disabled={busy}
            onChange={(event) => onBaudRateChange(Number(event.target.value))}
            helperText="Match the printer’s serial setting (self-test page)."
            sx={{ minWidth: 140 }}
          >
            {SERIAL_BAUD_RATES.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      {transport === 'windows-raw' && (
        <Alert severity="info">
          Receipts are sent to this Windows print queue as raw printer data. Windows reports whether it
          accepted the job, not whether paper came out; paper and cover status appear only when the
          Windows driver reports them.
        </Alert>
      )}

      {check !== 'checking' && (
        <Box>
          <LoadingButton
            variant={tested ? 'outlined' : 'contained'}
            loading={test === 'printing'}
            disabled={busy || check === 'unreachable'}
            onClick={onTestPrint}
            startIcon={<MuiIcon icon={MuiDescriptionIcon} />}
          >
            {tested ? 'Print another test page' : 'Print test page'}
          </LoadingButton>
          {tested && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              {candidate.testResult?.acceptedBy === 'spooler'
                ? 'Test page accepted by the Windows print queue'
                : 'Test page sent'}
              {candidate.testResult?.message ? ` — ${candidate.testResult.message}` : '.'} If it came out of
              the printer, save it.
            </Alert>
          )}
          {test === 'failed' && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {candidate.testError}
            </Alert>
          )}
        </Box>
      )}

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ pt: 1 }}>
        <Button onClick={onBack} disabled={saving}>
          Back
        </Button>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
          <Button color="inherit" disabled={busy} onClick={onSave}>
            {tested ? 'Save' : 'Save without a test page'}
          </Button>
          <LoadingButton variant="contained" loading={saving} disabled={busy} onClick={onSaveAndUse}>
            {tested ? 'Save and use' : 'Save and use anyway'}
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
}
